if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
    constructor() {
      super();

      // The drawer class might be on the custom element itself or on a child
      this.drawerInner = this.querySelector('.drawer__inner');
      this.overlay = this.querySelector('.cart-drawer__overlay');

      if (this.overlay) {
        this.overlay.addEventListener('click', this.close.bind(this));
      }

      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') {
          this.close();
        }
      });

      // Handle close button clicks - use event delegation
      this.addEventListener('click', (evt) => {
        const closeButton = evt.target.closest('.drawer__close');
        if (closeButton) {
          this.close();
        }
      });

      this.init();
    }

    init() {
      // Listen for cart:updated events from product-form
      document.addEventListener('cart:updated', this.onCartUpdated.bind(this));
      // Audit 2026-10-08 (A3/A4): the drawer body is replaced wholesale by the Section
      // Rendering API on every cart change (product-form, cart-system, upsell-system), which
      // (a) re-created the countdown at its full duration without ever starting it and
      // (b) never re-checked gift lines. Watch for re-renders and re-sync both.
      this._syncScheduled = null;
      this._observer = new MutationObserver((records) => {
        // ignore our own once-a-second countdown repaint, otherwise this would re-check the cart every second
        if (records.every(r => r.target && r.target.nodeType === 1 ? r.target.closest('countdown-timer') : (r.target.parentElement && r.target.parentElement.closest('countdown-timer')))) return;
        this.scheduleSync();
      });
      this._observer.observe(this, { childList: true, subtree: true });
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.scheduleSync());
      }
      this.scheduleSync();
    }

    async onCartUpdated(event) {
      // The product-form handles the section update,
      // but we can re-initialize any scripts if needed
      this.reinitializeScripts();
      this.scheduleSync();
    }

    scheduleSync() {
      if (this._syncScheduled) clearTimeout(this._syncScheduled);
      this._syncScheduled = setTimeout(() => {
        this._syncScheduled = null;
        this.syncTimer();
        this.pruneOrphanGifts();
      }, 250);
    }

    // A3: "cart reserved for MM:SS" — one deadline per browser session, counted down by us
    // (Shrine's CountdownTimer only plays with data-auto-play and restarts on every render).
    syncTimer() {
      const el = this.querySelector('countdown-timer');
      if (!el || el._chugSynced) return;
      el._chugSynced = true;
      const initial = parseInt(el.duration || el.getAttribute('data-duration'), 10) || 350;
      const key = 'chug_cart_timer_deadline';
      const now = Date.now();
      let deadline = 0;
      try { deadline = parseInt(sessionStorage.getItem(key) || '0', 10); } catch (e) { deadline = 0; }
      if (!deadline || deadline <= now) {
        deadline = now + initial * 1000;
        try { sessionStorage.setItem(key, String(deadline)); } catch (e) { /* private mode */ }
      }
      if (el.playInterval) { clearInterval(el.playInterval); el.isPlaying = false; }
      if (!el.minutesSpan || !el.secondsSpan) {
        el.innerHTML = '';
        el.minutesSpan = document.createElement('span'); el.secondsSpan = document.createElement('span');
        el.append(el.minutesSpan, document.createTextNode(':'), el.secondsSpan);
      }
      const pad = (n) => (n < 10 ? '0' : '') + n;
      const paint = () => {
        const r = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        el.minutesSpan.textContent = pad(Math.floor(r / 60));
        el.secondsSpan.textContent = pad(r % 60);
        if (r <= 0 && el._chugTick) { clearInterval(el._chugTick); el._chugTick = null; }
      };
      paint();
      if (el._chugTick) clearInterval(el._chugTick);
      el._chugTick = setInterval(paint, 1000);
    }

    // A4: a gift line (added with `_gift_for` = the unlocking variant id) must leave the cart
    // when that variant is no longer in it.
    async pruneOrphanGifts() {
      if (this._pruning) return;
      this._pruning = true;
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        const variants = new Set(cart.items.map(i => String(i.variant_id)));
        const orphans = cart.items.filter(i => i.properties && i.properties._gift_for && !variants.has(String(i.properties._gift_for)));
        if (!orphans.length) return;
        for (const o of orphans) {
          await fetch('/cart/change.js', { method: 'POST', headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: o.key, quantity: 0 }) });
        }
        const removed = orphans.reduce((n, o) => n + o.quantity, 0);
        document.querySelectorAll('.cart-count-bubble span:first-child').forEach(b => {
          const n = Math.max(0, (parseInt(b.textContent, 10) || cart.item_count) - removed);
          b.textContent = n < 100 ? n : '99+';
        });
        if (document.getElementById('main-cart-items')) { window.location.reload(); return; }
        await this.refreshDrawer();
      } catch (e) {
        /* best-effort: never block the drawer */
      } finally {
        this._pruning = false;
      }
    }

    async refreshDrawer() {
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const html = await fetch(cartUrl + '?section_id=cart-drawer').then(r => r.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
      const current = document.getElementById('CartDrawer');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }

    reinitializeScripts() {
      // Re-run any initialization scripts for the updated cart content
      // This handles quantity inputs, remove buttons, etc.
      const scripts = this.querySelectorAll('script');
      scripts.forEach(script => {
        if (script.src) {
          // External script - reload it
          const newScript = document.createElement('script');
          newScript.src = script.src;
          document.head.appendChild(newScript);
        } else if (script.textContent.trim()) {
          // Inline script - execute it
          eval(script.textContent);
        }
      });
    }

    open() {
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');

      // Focus the first focusable element
      const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Announce to screen readers
      this.setAttribute('aria-modal', 'true');
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      this.removeAttribute('aria-modal');
    }
  });
}
